import router from '@/router'
import useMetaStore from '@/stores/meta'
import usePreferenceStore from '@/stores/preference'
import useUserStore from '@/stores/user'
import { RequestError } from '@/types/bug-log'
import { CodeError, ErrorCodes } from '@/types/code-error'
import { handleApiError, type ApiErrorResponse } from '@/utils/error-handler'
import axios from 'axios'

const request = axios.create({
  timeoutErrorMessage: '请求超时，请检查网络后重试~',
  baseURL: import.meta.env.VITE_BASE_URL,
  // 加长默认超时时间，防止长耗时导入/导出请求被过早中断
  // 若偏好设置中的 requestTimeout 更大，会在拦截器中自动覆盖此值
  timeout: 120000,
})


request.interceptors.request.use((config) => {
  const { token } = useUserStore()
  const { requestTimeout } = usePreferenceStore()

  if (config.timeout === void 0 || config.timeout < requestTimeout * 1000) {
    config.timeout = requestTimeout * 1000
  }

  if (token) {
    // 统一使用 Bearer 前缀
    const hasBearer = typeof token === 'string' && token.trim().toLowerCase().startsWith('bearer ')
    config.headers = config.headers || {}
    ;(config.headers as Record<string, string>).Authorization = hasBearer ? token : `Bearer ${token}`
  }

  // 添加 App Key (生产和开发环境都需要)
  if (import.meta.env.VITE_APP_KEY) {
    config.headers = config.headers || {}
    ;(config.headers as Record<string, string>)['X-App-Key'] = import.meta.env.VITE_APP_KEY
  }

  return config
})

let activeRequests = 0
const requestQueue: Array<() => void> = []
request.interceptors.request.use((config) => {
  return new Promise((resolve) => {
    const { maxConcurrentRequestCount } = usePreferenceStore()

    if (activeRequests < maxConcurrentRequestCount) {
      activeRequests++
      resolve(config)
    } else {
      requestQueue.push(() => {
        activeRequests++
        resolve(config)
      })
    }
  })
})

const decreaseActiveRequests = () => {
  activeRequests--

  if (requestQueue.length > 0) {
    const nextRequest = requestQueue.shift()
    nextRequest!()
  }
}
request.interceptors.response.use(
  (response) => {
    decreaseActiveRequests()
    return response
  },
  (error) => {
    decreaseActiveRequests()
    throw error
  },
)

request.interceptors.response.use(
  (response) => response,
  async (error) => {
    const { addBugLog } = useMetaStore()
    addBugLog(new RequestError('network', error, Date.now()))

    throw error
  },
)

request.interceptors.response.use(
  (response) => {
    const userStore = useUserStore()
    const { redirectRoute } = storeToRefs(useMetaStore())

    // 处理Token刷新
    const token = response.headers.authorization || response.headers.Authorization
    if (token) {
      userStore.setToken(token)
    }

    const { data } = response

    // 兼容多种返回协议：
    // 1) 旧版本: { code, data, err_msg }
    // 2) 新版本: { success, data/message, timestamp }
    
    // 处理旧版本协议
    if (typeof data?.code === 'number') {
      if (data.code === 0) {
        return data.data
      }
      if (data.code === ErrorCodes.TokenInvalid) {
        redirectRoute.value = router.currentRoute.value
        userStore.loginOut()
        throw new Error('当前 Token 已过期，请重新登陆！')
      }
      const { addBugLog } = useMetaStore()
      addBugLog(new RequestError('api', response, Date.now()))
      throw new CodeError(data.code, data.err_msg)
    }

    // 处理新版本协议 (v2.4.0)
    if (typeof data?.success === 'boolean') {
      if (data.success) {
        // 对于登录接口，返回完整的响应数据（包含 accessToken）
        if (response.config.url?.includes('/auth/login')) {
          return data
        }
        // 其他接口返回 data 字段
        return data.data || data
      }
      
      // 检查是否为认证相关错误
      if (data.message?.includes('token') || data.message?.includes('认证') || data.message?.includes('登录')) {
        redirectRoute.value = router.currentRoute.value
        userStore.loginOut()
        throw new Error(data.message || '认证失败，请重新登录')
      }
      
      // 其他错误响应
      const error = new Error(data.message || '请求失败') as any
      error.response = response
      throw error
    }

    // 未知结构或直接返回数据
    return data
  },
  (error) => {
    const userStore = useUserStore()
    const { redirectRoute } = storeToRefs(useMetaStore())
    
    // 处理HTTP状态码401（未授权）
    if (error.response?.status === 401) {
      console.log('检测到401未授权，清除Token并跳转登录')
      redirectRoute.value = router.currentRoute.value
      userStore.loginOut()
      throw new Error('登录已过期，请重新登录')
    }
    
    // 处理HTTP状态码403（权限不足）
    if (error.response?.status === 403) {
      throw new Error('权限不足，无法访问此资源')
    }
    
    // 检查响应数据中的认证错误
    if (error.response?.data) {
      const { data } = error.response
      
      // 检查新版本协议的认证错误
      if (data.success === false && (
        data.message?.includes('token') ||
        data.message?.includes('认证') ||
        data.message?.includes('登录') ||
        data.message?.includes('授权')
      )) {
        console.log('检测到认证错误，清除Token并跳转登录')
        redirectRoute.value = router.currentRoute.value
        userStore.loginOut()
        throw new Error(data.message || '认证失败，请重新登录')
      }
      
      // 检查旧版本协议的Token错误
      if (data.code === ErrorCodes.TokenInvalid) {
        console.log('检测到Token无效，清除Token并跳转登录')
        redirectRoute.value = router.currentRoute.value
        userStore.loginOut()
        throw new Error('Token已过期，请重新登录')
      }
    }
    
    // 记录错误日志
    if (error.response) {
      const { addBugLog } = useMetaStore()
      addBugLog(new RequestError('api', error.response, Date.now()))
    }
    
    // 透传错误，让调用方处理
    throw error
  },
)

export default request
